import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { Mail, Phone, MessageCircle, Loader2, Send } from "lucide-react";

export function ContactSection() {
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", { ...f, category: "general" });
      toast.success(`Message sent · Ref ${data.ref}`);
      setF({ name: "", email: "", subject: "", message: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <section id="contact" className="bg-emerald-50/60 border-t border-emerald-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">Contact TMF</span>
          <h2 className="mt-3 font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-emerald-950 tracking-tight">
            Talk to the Text-Me-Food team.
          </h2>
          <p className="mt-4 text-gray-600 text-base leading-relaxed max-w-md">
            Sponsors, vendors, development partners and beneficiaries — reach us directly and we'll respond quickly.
          </p>
          <div className="mt-8 space-y-4">
            <a href="mailto:info@textmefood.org" data-testid="contact-email-link" className="flex items-center gap-3 group">
              <span className="w-11 h-11 rounded-xl bg-white border border-emerald-100 flex items-center justify-center"><Mail className="w-5 h-5 text-emerald-600" /></span>
              <span className="text-emerald-950 font-medium group-hover:text-emerald-600 transition-colors">info@textmefood.org</span>
            </a>
            <a href="tel:+2348121423424" data-testid="contact-phone-link" className="flex items-center gap-3 group">
              <span className="w-11 h-11 rounded-xl bg-white border border-emerald-100 flex items-center justify-center"><Phone className="w-5 h-5 text-emerald-600" /></span>
              <span className="text-emerald-950 font-medium group-hover:text-emerald-600 transition-colors">+234 812 142 3424</span>
            </a>
            <a href="https://wa.me/2348121423424" target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-3 group">
              <span className="w-11 h-11 rounded-xl bg-white border border-emerald-100 flex items-center justify-center"><MessageCircle className="w-5 h-5 text-emerald-600" /></span>
              <span className="text-emerald-950 font-medium group-hover:text-emerald-600 transition-colors">WhatsApp +234 812 142 3424</span>
            </a>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl bg-white border border-emerald-100 p-6 sm:p-8 shadow-sm space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Full Name</Label><Input data-testid="contact-name" value={f.name} onChange={(e) => set("name", e.target.value)} required className="mt-1.5 h-11" /></div>
            <div><Label>Email</Label><Input data-testid="contact-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} required className="mt-1.5 h-11" /></div>
          </div>
          <div><Label>Subject</Label><Input data-testid="contact-subject" value={f.subject} onChange={(e) => set("subject", e.target.value)} className="mt-1.5 h-11" /></div>
          <div><Label>Message</Label><Textarea data-testid="contact-message" value={f.message} onChange={(e) => set("message", e.target.value)} required rows={4} className="mt-1.5" /></div>
          <Button data-testid="contact-submit" type="submit" disabled={loading} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 rounded-full">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Send Message <Send className="w-4 h-4 ml-2" /></>}
          </Button>
        </form>
      </div>
    </section>
  );
}
